// Live charts (uPlot) — the read-out for an evolving ecosystem.
//
// Three things here are deliberate, and each one exists because the obvious
// version of the chart hides the thing you actually want to see:
//
//   1. WOLVES GET THEIR OWN Y AXIS. There are 10-100x fewer wolves than deer,
//      so on a shared axis the predator is a flat line pinned to the floor and
//      the whole predator-prey story is invisible.
//
//   2. THERE IS A PHASE PORTRAIT. Deer on x, wolves on y, drawn as a fading
//      trajectory. A Lotka-Volterra system traces a closed loop when it is
//      stable, an inward spiral when it is damping, and an outward one when it
//      is ringing itself to extinction — none of which you can read off two
//      time series side by side. uPlot cannot draw it (it requires a sorted x),
//      so it is a small hand-rolled canvas.
//
//   3. TRAITS ARE DRAWN AS MEAN ± 1 SD. A mean alone cannot distinguish a
//      population converging on one strategy from one splitting into two, and
//      that distinction is most of what selection is doing.
//
// Colour identity is fixed across every chart and matches the renderer: deer
// are the warm tan, wolves the cool slate, grass green. All colour comes from
// the CSS custom properties in style.css, read at runtime, so a theme change
// flows through.

import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { REF_MASS_KG } from "../sim/scale";

// Plain time-series arrays as posted by the sim worker (uPlot-shaped).
// Mirrors SERIES_KEYS in src/sim/stats.ts.
export interface StatsData {
  t: number[];
  deer: number[];
  wolves: number[];
  grass: number[];
  deerSpeed: number[];
  deerSpeedSd: number[];
  deerSize: number[];
  deerSizeSd: number[];
  deerSense: number[];
  deerSenseSd: number[];
  deerBrain: number[];
  deerBrainSd: number[];
  deerEnergy: number[];
  deerAge: number[];
  wolfSpeed: number[];
  wolfSpeedSd: number[];
  wolfSize: number[];
  wolfSizeSd: number[];
  wolfSense: number[];
  wolfSenseSd: number[];
  wolfBrain: number[];
  wolfBrainSd: number[];
  wolfEnergy: number[];
  wolfAge: number[];
  birthRate: number[];
  deathRate: number[];
  killRate: number[];
  deerBirthRate: number[];
  wolfBirthRate: number[];
  huntSuccess: number[];
}

type SeriesKey = keyof StatsData;

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------

const TOKEN_FALLBACK: Record<string, string> = {
  "--bg": "#0b0f0a",
  "--panel": "#131811",
  "--panel-2": "#1a2017",
  "--line": "#2a3327",
  "--ink": "#dde5d4",
  "--muted": "#8a9580",
  "--dim": "#5f6b57",
  "--deer": "#d8ab6a",
  "--wolf": "#93a6ba",
  "--grass": "#6fbf5c",
  "--accent": "#6fbf5c",
  "--warn": "#e0a458",
  "--danger": "#d4675a",
};

let tokens: Record<string, string> | null = null;

/** Read a design token from :root, falling back to the documented value. */
function tok(name: string): string {
  if (!tokens) {
    tokens = {};
    const cs = getComputedStyle(document.documentElement);
    for (const k of Object.keys(TOKEN_FALLBACK)) {
      tokens[k] = cs.getPropertyValue(k).trim() || TOKEN_FALLBACK[k];
    }
  }
  return tokens[name] ?? TOKEN_FALLBACK[name] ?? "#888888";
}

/** Drop the cache so the next paint picks up a theme change. */
function invalidateTokens(): void {
  tokens = null;
}

/** #rgb / #rrggbb → rgba(). Anything else is passed through untouched. */
function alpha(color: string, a: number): string {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  if (!m) return color;
  let h = m[1];
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

const DEER = () => tok("--deer");
const WOLF = () => tok("--wolf");
const GRASS = () => tok("--grass");
const ACCENT = () => tok("--accent");
const WARN = () => tok("--warn");
const DANGER = () => tok("--danger");

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const f0 = (v: number): string => (Number.isFinite(v) ? Math.round(v).toString() : "–");
const f1 = (v: number): string => (Number.isFinite(v) ? v.toFixed(1) : "–");
const f2 = (v: number): string => (Number.isFinite(v) ? v.toFixed(2) : "–");
const pct = (v: number): string => (Number.isFinite(v) ? `${(v * 100).toFixed(0)}%` : "–");
const kg = (v: number): string =>
  Number.isFinite(v) ? `${(v * REF_MASS_KG).toFixed(0)} kg` : "–";

// ---------------------------------------------------------------------------
// Chart specs
// ---------------------------------------------------------------------------

type RangeMode = "zero" | "unit" | "auto";

interface LineSpec {
  key: SeriesKey;
  /** When set, a ±1 SD band is filled around `key` from this series. */
  sdKey?: SeriesKey;
  label: string;
  color: () => string;
  /** Put this line on the secondary (right-hand) y scale. */
  right?: boolean;
  fmt?: (v: number) => string;
  dash?: number[];
}

interface TimeSpec {
  kind: "time";
  title: string;
  /** Left y-axis label — this is where the units live. */
  yLabel: string;
  y2Label?: string;
  lines: LineSpec[];
  yRange?: RangeMode;
  y2Range?: RangeMode;
  /** Tick text for the left axis (defaults to uPlot's own). */
  yTick?: (v: number) => string;
  y2Tick?: (v: number) => string;
  /** One line of "what am I looking at", shown under the title. */
  note?: string;
}

interface PhaseSpec {
  kind: "phase";
  title: string;
  note?: string;
}

type ChartSpec = TimeSpec | PhaseSpec;

interface GroupSpec {
  name: string;
  /** Collapsed by default on narrow screens regardless of this flag. */
  open: boolean;
  charts: ChartSpec[];
}

const GROUPS: GroupSpec[] = [
  {
    name: "Ecosystem",
    open: true,
    charts: [
      {
        kind: "time",
        title: "Population",
        note: "wolves on the right-hand axis — they run 10-100× rarer",
        yLabel: "deer (count)",
        y2Label: "wolves (count)",
        yRange: "zero",
        y2Range: "zero",
        lines: [
          { key: "deer", label: "Deer", color: DEER, fmt: f0 },
          { key: "wolves", label: "Wolves", color: WOLF, right: true, fmt: f0 },
        ],
      },
      {
        kind: "phase",
        title: "Phase portrait — deer × wolves",
        note: "a loop means a stable cycle; a widening spiral means it is ringing",
      },
      {
        kind: "time",
        title: "Grass",
        yLabel: "biomass (full cells)",
        yRange: "zero",
        lines: [{ key: "grass", label: "Grass", color: GRASS, fmt: f0 }],
      },
    ],
  },
  {
    name: "Vital rates",
    open: true,
    charts: [
      {
        kind: "time",
        title: "Births & deaths",
        note: "smoothed over ~10 sim-seconds",
        yLabel: "events / sim-s",
        yRange: "zero",
        lines: [
          { key: "birthRate", label: "Births", color: ACCENT, fmt: f2 },
          { key: "deathRate", label: "Deaths", color: DANGER, fmt: f2 },
        ],
      },
      {
        kind: "time",
        title: "Recruitment by species",
        note: "is each species actually replacing itself?",
        yLabel: "deer births / s",
        y2Label: "wolf births / s",
        yRange: "zero",
        y2Range: "zero",
        lines: [
          { key: "deerBirthRate", label: "Deer", color: DEER, fmt: f2 },
          { key: "wolfBirthRate", label: "Wolves", color: WOLF, right: true, fmt: f2 },
        ],
      },
      {
        kind: "time",
        title: "Predation",
        note: "realised kills ÷ attacks — real wolves fail 80–90% of the time",
        yLabel: "hunt success",
        y2Label: "kills / sim-s",
        yRange: "unit",
        y2Range: "zero",
        yTick: pct,
        lines: [
          { key: "huntSuccess", label: "Success", color: WARN, fmt: pct },
          { key: "killRate", label: "Kills", color: DANGER, right: true, fmt: f2 },
        ],
      },
      {
        kind: "time",
        title: "Condition",
        note: "mean energy as a fraction of each animal's own tank",
        yLabel: "reserves (% of tank)",
        yRange: "unit",
        yTick: pct,
        lines: [
          { key: "deerEnergy", label: "Deer", color: DEER, fmt: pct },
          { key: "wolfEnergy", label: "Wolves", color: WOLF, fmt: pct },
        ],
      },
      {
        kind: "time",
        title: "Age structure",
        note: "low = culled young (predation), high = dying of old age",
        yLabel: "mean age (% of lifespan)",
        yRange: "unit",
        yTick: pct,
        lines: [
          { key: "deerAge", label: "Deer", color: DEER, fmt: pct },
          { key: "wolfAge", label: "Wolves", color: WOLF, fmt: pct },
        ],
      },
    ],
  },
  {
    name: "Evolution",
    open: true,
    charts: [
      {
        kind: "time",
        title: "Body mass",
        note: "line = mean, band = ±1 SD across the living population",
        yLabel: "mass (kg)",
        yRange: "auto",
        yTick: (v) => (v * REF_MASS_KG).toFixed(0),
        lines: [
          { key: "deerSize", sdKey: "deerSizeSd", label: "Deer", color: DEER, fmt: kg },
          { key: "wolfSize", sdKey: "wolfSizeSd", label: "Wolves", color: WOLF, fmt: kg },
        ],
      },
      {
        kind: "time",
        title: "Top speed",
        yLabel: "sprint (m/s)",
        yRange: "auto",
        lines: [
          { key: "deerSpeed", sdKey: "deerSpeedSd", label: "Deer", color: DEER, fmt: f1 },
          { key: "wolfSpeed", sdKey: "wolfSpeedSd", label: "Wolves", color: WOLF, fmt: f1 },
        ],
      },
      {
        kind: "time",
        title: "Sense radius",
        note: "keen senses are billed twice — organs plus the brain to use them",
        yLabel: "detection (m)",
        yRange: "auto",
        lines: [
          { key: "deerSense", sdKey: "deerSenseSd", label: "Deer", color: DEER, fmt: f0 },
          { key: "wolfSense", sdKey: "wolfSenseSd", label: "Wolves", color: WOLF, fmt: f0 },
        ],
      },
      {
        kind: "time",
        title: "Brain mass",
        note: "fraction of the maximum topology each animal switches on and pays for",
        yLabel: "brain (frac. of max)",
        yRange: "auto",
        yTick: f2,
        lines: [
          { key: "deerBrain", sdKey: "deerBrainSd", label: "Deer", color: DEER, fmt: f2 },
          { key: "wolfBrain", sdKey: "wolfBrainSd", label: "Wolves", color: WOLF, fmt: f2 },
        ],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Scoped CSS. Only classes this module owns — `.chart-card` and its `h3` stay
// the property of style.css.
// ---------------------------------------------------------------------------

const CSS = `
.chart-root { position: relative; display: flex; flex-direction: column; gap: 8px; }
.chart-group { border: 1px solid var(--line, #2a3327); border-radius: 8px;
  background: var(--panel-2, #1a2017); overflow: hidden; }
.chart-group > summary { list-style: none; cursor: pointer; padding: 7px 10px;
  font-size: 11px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase;
  color: var(--muted, #8a9580); user-select: none; display: flex; align-items: center; gap: 7px; }
.chart-group > summary::-webkit-details-marker { display: none; }
.chart-group > summary::before { content: "\\25B8"; font-size: 10px;
  color: var(--dim, #5f6b57); transition: transform .15s ease; }
.chart-group[open] > summary::before { transform: rotate(90deg); }
.chart-group > summary:hover { color: var(--ink, #dde5d4); }
.chart-group-body { display: flex; flex-direction: column; gap: 8px; padding: 0 8px 8px; }
.chart-note { margin: -2px 0 4px; font-size: 10px; line-height: 1.3;
  color: var(--dim, #5f6b57); }
.chart-legend { display: flex; flex-wrap: wrap; gap: 2px 10px; margin: 0 0 3px;
  font-size: 10px; color: var(--muted, #8a9580); }
.chart-legend-item { display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; }
.chart-swatch { width: 9px; height: 3px; border-radius: 2px; flex: none; }
.chart-legend-value { color: var(--ink, #dde5d4); font-variant-numeric: tabular-nums; }
.chart-phase { display: block; width: 100%; touch-action: pan-y; }
.chart-tip { position: absolute; z-index: 6; pointer-events: none; display: none;
  background: var(--panel, #131811); border: 1px solid var(--line, #2a3327);
  border-radius: 5px; padding: 4px 7px; font-size: 10px; line-height: 1.45;
  color: var(--ink, #dde5d4); white-space: nowrap; box-shadow: 0 3px 12px rgba(0,0,0,.55); }
.chart-tip .tip-t { color: var(--muted, #8a9580); }
.chart-tip .tip-v { color: var(--ink, #dde5d4); font-variant-numeric: tabular-nums; }
.chart-root .u-legend { display: none; }
`;

function injectCss(): void {
  if (document.getElementById("ecosim-charts-css")) return;
  const el = document.createElement("style");
  el.id = "ecosim-charts-css";
  el.textContent = CSS;
  document.head.appendChild(el);
}

// ---------------------------------------------------------------------------
// Phase portrait (hand-rolled: uPlot needs a sorted x, and a trajectory is not)
// ---------------------------------------------------------------------------

const PHASE_WINDOW = 900; // samples of trail to draw
const PHASE_CHUNKS = 14; // fade steps along the trail

class PhasePlot {
  readonly el: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D | null;
  private w = 300;
  private h = 190;

  constructor() {
    this.el = document.createElement("canvas");
    this.el.className = "chart-phase";
    this.ctx = this.el.getContext("2d");
  }

  setSize(w: number, h: number): void {
    this.w = Math.max(80, w);
    this.h = Math.max(80, h);
    const dpr = window.devicePixelRatio || 1;
    this.el.width = Math.round(this.w * dpr);
    this.el.height = Math.round(this.h * dpr);
    this.el.style.width = `${this.w}px`;
    this.el.style.height = `${this.h}px`;
  }

  draw(s: StatsData | null, hoverIdx: number | null): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, this.w, this.h);

    const padL = 38;
    const padR = 10;
    const padT = 10;
    const padB = 26;
    const iw = this.w - padL - padR;
    const ih = this.h - padT - padB;
    if (iw <= 10 || ih <= 10) return;

    const len = s ? s.deer.length : 0;
    const start = Math.max(0, len - PHASE_WINDOW);
    const n = len - start;

    // Bounds over the drawn window.
    let maxD = 1;
    let maxW = 1;
    for (let i = start; i < len; i++) {
      if (s!.deer[i] > maxD) maxD = s!.deer[i];
      if (s!.wolves[i] > maxW) maxW = s!.wolves[i];
    }
    maxD = niceCeil(maxD * 1.08);
    maxW = niceCeil(maxW * 1.08);

    const x = (v: number): number => padL + (v / maxD) * iw;
    const y = (v: number): number => padT + ih - (v / maxW) * ih;

    // --- Frame, grid and ticks ---------------------------------------------
    ctx.lineWidth = 1;
    ctx.strokeStyle = alpha(tok("--line"), 0.9);
    ctx.font = "9px ui-monospace, Menlo, Consolas, monospace";
    ctx.fillStyle = tok("--dim");

    ctx.beginPath();
    for (let i = 0; i <= 4; i++) {
      const gx = Math.round(padL + (iw * i) / 4) + 0.5;
      ctx.moveTo(gx, padT);
      ctx.lineTo(gx, padT + ih);
      const gy = Math.round(padT + (ih * i) / 4) + 0.5;
      ctx.moveTo(padL, gy);
      ctx.lineTo(padL + iw, gy);
    }
    ctx.stroke();

    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    for (let i = 0; i <= 4; i += 2) {
      ctx.fillText(f0((maxD * i) / 4), padL + (iw * i) / 4, padT + ih + 4);
    }
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    for (let i = 0; i <= 4; i += 2) {
      ctx.fillText(f0((maxW * i) / 4), padL - 5, padT + ih - (ih * i) / 4);
    }

    // Axis titles carry the species colour, so identity survives the switch
    // from "time on x" to "deer on x".
    ctx.textAlign = "right";
    ctx.textBaseline = "bottom";
    ctx.fillStyle = tok("--deer");
    ctx.fillText("deer →", padL + iw, this.h - 2);
    ctx.save();
    ctx.translate(9, padT + ih);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillStyle = tok("--wolf");
    ctx.fillText("wolves →", 0, 0);
    ctx.restore();

    if (n < 2) {
      ctx.fillStyle = tok("--dim");
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("collecting…", padL + iw / 2, padT + ih / 2);
      return;
    }

    // --- Trajectory, oldest faint → newest bright ---------------------------
    ctx.save();
    ctx.beginPath();
    ctx.rect(padL, padT, iw, ih);
    ctx.clip();
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    const accent = tok("--accent");
    for (let c = 0; c < PHASE_CHUNKS; c++) {
      const a0 = start + Math.floor(((n - 1) * c) / PHASE_CHUNKS);
      const a1 = start + Math.floor(((n - 1) * (c + 1)) / PHASE_CHUNKS);
      if (a1 <= a0) continue;
      const f = (c + 1) / PHASE_CHUNKS;
      ctx.beginPath();
      ctx.moveTo(x(s!.deer[a0]), y(s!.wolves[a0]));
      for (let i = a0 + 1; i <= a1; i++) ctx.lineTo(x(s!.deer[i]), y(s!.wolves[i]));
      ctx.strokeStyle = alpha(accent, 0.07 + 0.83 * f * f);
      ctx.lineWidth = 0.7 + 1.1 * f;
      ctx.stroke();
    }

    // Head of the trail, with a direction tick so travel is unambiguous.
    const hx = x(s!.deer[len - 1]);
    const hy = y(s!.wolves[len - 1]);
    const px = x(s!.deer[Math.max(start, len - 6)]);
    const py = y(s!.wolves[Math.max(start, len - 6)]);
    const dx = hx - px;
    const dy = hy - py;
    const dl = Math.hypot(dx, dy);
    if (dl > 2) {
      const ux = dx / dl;
      const uy = dy / dl;
      ctx.beginPath();
      ctx.moveTo(hx + ux * 7, hy + uy * 7);
      ctx.lineTo(hx - uy * 4, hy + ux * 4);
      ctx.lineTo(hx + uy * 4, hy - ux * 4);
      ctx.closePath();
      ctx.fillStyle = tok("--ink");
      ctx.fill();
    } else {
      ctx.beginPath();
      ctx.arc(hx, hy, 3, 0, Math.PI * 2);
      ctx.fillStyle = tok("--ink");
      ctx.fill();
    }

    // Where the shared cursor is pointing, if anywhere.
    if (hoverIdx != null && hoverIdx >= start && hoverIdx < len) {
      ctx.beginPath();
      ctx.arc(x(s!.deer[hoverIdx]), y(s!.wolves[hoverIdx]), 4, 0, Math.PI * 2);
      ctx.strokeStyle = tok("--warn");
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
    ctx.restore();
  }
}

function niceCeil(v: number): number {
  if (!Number.isFinite(v) || v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / mag;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * mag;
}

// ---------------------------------------------------------------------------
// uPlot construction
// ---------------------------------------------------------------------------

const SYNC_KEY = "ecosim";
const FONT = "9px ui-monospace, Menlo, Consolas, monospace";

function rangeFn(mode: RangeMode): (
  u: uPlot,
  dmin: number,
  dmax: number,
) => [number, number] {
  if (mode === "unit") return () => [0, 1];
  return (_u, dmin, dmax) => {
    if (!Number.isFinite(dmin) || !Number.isFinite(dmax)) return [0, 1];
    let lo = mode === "zero" ? 0 : dmin;
    let hi = dmax;
    if (hi - lo < 1e-9) hi = lo + Math.max(1, Math.abs(lo) * 0.1);
    const pad = (hi - lo) * 0.08;
    return [mode === "zero" ? 0 : lo - pad, hi + pad];
  };
}

/** One legend entry — a swatch, a label and the value at the cursor. */
interface LegendItem {
  key: SeriesKey;
  label: string;
  color: () => string;
  fmt: (v: number) => string;
  swatch: HTMLElement;
  value: HTMLElement;
}

interface TimeEntry {
  kind: "time";
  spec: TimeSpec;
  plot: uPlot;
  mount: HTMLElement;
  /** Builders parallel to uPlot series 1..n (index 0 is the shared x). */
  build: ((s: StatsData) => number[])[];
  legend: LegendItem[];
  group: GroupEntry;
}

interface PhaseEntry {
  kind: "phase";
  phase: PhasePlot;
  legend: LegendItem[];
  group: GroupEntry;
}

type Entry = TimeEntry | PhaseEntry;

interface GroupEntry {
  details: HTMLDetailsElement;
  entries: Entry[];
}

function makeLegend(
  parent: HTMLElement,
  lines: { key: SeriesKey; label: string; color: () => string; fmt?: (v: number) => string }[],
): LegendItem[] {
  const row = document.createElement("div");
  row.className = "chart-legend";
  const items: LegendItem[] = [];
  for (const l of lines) {
    const item = document.createElement("span");
    item.className = "chart-legend-item";
    const sw = document.createElement("i");
    sw.className = "chart-swatch";
    sw.style.background = l.color();
    const lab = document.createElement("span");
    lab.textContent = l.label;
    const val = document.createElement("span");
    val.className = "chart-legend-value";
    val.textContent = "–";
    item.append(sw, lab, val);
    row.appendChild(item);
    items.push({
      key: l.key,
      label: l.label,
      color: l.color,
      fmt: l.fmt ?? f1,
      swatch: sw,
      value: val,
    });
  }
  parent.appendChild(row);
  return items;
}

// ---------------------------------------------------------------------------

export class Charts {
  private root: HTMLElement;
  private tip: HTMLElement;
  private groups: GroupEntry[] = [];
  private timeEntries: TimeEntry[] = [];
  private phaseEntries: PhaseEntry[] = [];
  private last: StatsData | null = null;
  private hoverIdx: number | null = null;
  private active: TimeEntry | null = null;
  private width: number;

  constructor(container: HTMLElement) {
    injectCss();
    this.width = container.clientWidth || 320;

    this.root = document.createElement("div");
    this.root.className = "chart-root";
    container.appendChild(this.root);

    this.tip = document.createElement("div");
    this.tip.className = "chart-tip";
    this.root.appendChild(this.tip);

    const narrow = window.innerWidth <= 860;

    for (const g of GROUPS) {
      const details = document.createElement("details");
      details.className = "chart-group";
      details.open = narrow ? g === GROUPS[0] : g.open;
      const summary = document.createElement("summary");
      summary.textContent = g.name;
      details.appendChild(summary);
      const body = document.createElement("div");
      body.className = "chart-group-body";
      details.appendChild(body);
      this.root.appendChild(details);

      const group: GroupEntry = { details, entries: [] };
      this.groups.push(group);

      for (const spec of g.charts) {
        const card = document.createElement("div");
        card.className = "chart-card";
        const h = document.createElement("h3");
        h.textContent = spec.title;
        card.appendChild(h);
        if (spec.note) {
          const p = document.createElement("p");
          p.className = "chart-note";
          p.textContent = spec.note;
          card.appendChild(p);
        }
        body.appendChild(card);

        if (spec.kind === "phase") {
          const legend = makeLegend(card, [
            { key: "deer", label: "Deer", color: DEER, fmt: f0 },
            { key: "wolves", label: "Wolves", color: WOLF, fmt: f0 },
          ]);
          const phase = new PhasePlot();
          card.appendChild(phase.el);
          phase.setSize(this.plotWidth(), this.phaseHeight());
          phase.draw(null, null);
          const entry: PhaseEntry = { kind: "phase", phase, legend, group };
          group.entries.push(entry);
          this.phaseEntries.push(entry);
        } else {
          const legend = makeLegend(card, spec.lines);
          const mount = document.createElement("div");
          card.appendChild(mount);
          const entry = this.makeTimePlot(spec, mount, group, legend);
          group.entries.push(entry);
          this.timeEntries.push(entry);
        }
      }

      details.addEventListener("toggle", () => {
        if (!details.open) return;
        // A collapsed group has zero width, so its plots were sized to nothing.
        this.resizeGroup(group);
        this.refreshGroup(group);
      });
    }
  }

  // --- Construction helpers --------------------------------------------------

  private plotWidth(): number {
    return Math.max(120, this.width - 30);
  }

  private plotHeight(): number {
    return window.innerWidth <= 860 ? 134 : 152;
  }

  private phaseHeight(): number {
    return window.innerWidth <= 860 ? 168 : 196;
  }

  private makeTimePlot(
    spec: TimeSpec,
    mount: HTMLElement,
    group: GroupEntry,
    legend: LegendItem[],
  ): TimeEntry {
    const series: uPlot.Series[] = [{}];
    const bands: uPlot.Band[] = [];
    const build: ((s: StatsData) => number[])[] = [];
    let hasRight = false;

    for (const l of spec.lines) {
      const scale = l.right ? "y2" : "y";
      if (l.right) hasRight = true;

      if (l.sdKey) {
        // Two invisible edge series plus a band between them. Reused buffers:
        // update() runs several times a second over up to 3000 points, and
        // allocating four fresh arrays per chart per frame is pure garbage.
        const meanKey = l.key;
        const sdKey = l.sdKey;
        const hiBuf: number[] = [];
        const loBuf: number[] = [];
        const edge = (): uPlot.Series => ({
          scale,
          stroke: () => alpha(l.color(), 0.3),
          width: 0.5,
          points: { show: false },
        });
        const hiIdx = series.length;
        series.push(edge());
        build.push((s) => fillEdge(hiBuf, s[meanKey], s[sdKey], 1));
        const loIdx = series.length;
        series.push(edge());
        build.push((s) => fillEdge(loBuf, s[meanKey], s[sdKey], -1));
        bands.push({ series: [hiIdx, loIdx], fill: () => alpha(l.color(), 0.15) });
      }

      series.push({
        label: l.label,
        scale,
        stroke: () => l.color(),
        width: 1.6,
        dash: l.dash,
        points: { show: false },
      });
      const key = l.key;
      build.push((s) => s[key]);
    }

    const axes: uPlot.Axis[] = [
      {
        stroke: () => tok("--muted"),
        grid: { stroke: () => alpha(tok("--line"), 0.85), width: 1 },
        ticks: { show: false },
        size: 22,
        font: FONT,
        label: "sim time (s)",
        labelSize: 12,
        labelFont: FONT,
        labelGap: 0,
      },
      {
        scale: "y",
        stroke: () => tok("--muted"),
        grid: { stroke: () => alpha(tok("--line"), 0.85), width: 1 },
        ticks: { show: false },
        size: 34,
        font: FONT,
        label: spec.yLabel,
        labelSize: 12,
        labelFont: FONT,
        labelGap: 0,
        values: spec.yTick
          ? (_u, splits) => splits.map((v) => spec.yTick!(v))
          : undefined,
      },
    ];
    if (hasRight) {
      axes.push({
        scale: "y2",
        side: 1,
        stroke: () => tok("--muted"),
        grid: { show: false },
        ticks: { show: false },
        size: 34,
        font: FONT,
        label: spec.y2Label ?? "",
        labelSize: 12,
        labelFont: FONT,
        labelGap: 0,
        values: spec.y2Tick
          ? (_u, splits) => splits.map((v) => spec.y2Tick!(v))
          : undefined,
      });
    }

    const scales: uPlot.Scales = {
      x: { time: false },
      y: { range: rangeFn(spec.yRange ?? "auto") },
    };
    if (hasRight) scales.y2 = { range: rangeFn(spec.y2Range ?? "auto") };

    const entryRef: { e: TimeEntry | null } = { e: null };

    const opts: uPlot.Options = {
      width: this.plotWidth(),
      height: this.plotHeight(),
      scales,
      axes,
      series,
      bands,
      legend: { show: false },
      cursor: {
        y: false,
        points: { show: false },
        drag: { x: false, y: false, setScale: false },
        sync: { key: SYNC_KEY, scales: ["x", null], setSeries: false },
      },
      hooks: {
        setCursor: [
          (u) => {
            if (entryRef.e) this.onCursor(entryRef.e, u);
          },
        ],
      },
    };

    const plot = new uPlot(opts, emptyData(series.length), mount);
    const entry: TimeEntry = { kind: "time", spec, plot, mount, build, legend, group };
    entryRef.e = entry;

    plot.over.addEventListener("mouseenter", () => {
      this.active = entry;
    });
    plot.over.addEventListener("mouseleave", () => {
      if (this.active === entry) this.active = null;
      this.tip.style.display = "none";
      if (this.hoverIdx !== null) {
        this.hoverIdx = null;
        this.refreshAll();
      }
    });

    return entry;
  }

  // --- Cursor / tooltip ------------------------------------------------------

  private onCursor(entry: TimeEntry, u: uPlot): void {
    const idx = u.cursor.idx ?? null;
    if (idx !== this.hoverIdx) {
      this.hoverIdx = idx;
      this.refreshAll();
    }
    if (this.active !== entry || idx == null || !this.last) {
      if (this.active === entry) this.tip.style.display = "none";
      return;
    }
    this.showTip(entry, u, idx);
  }

  private showTip(entry: TimeEntry, u: uPlot, idx: number): void {
    const s = this.last!;
    const parts: string[] = [
      `<span class="tip-t">t = ${f0(s.t[idx] ?? 0)} s</span>`,
    ];
    for (const item of entry.legend) {
      const v = s[item.key]?.[idx];
      parts.push(
        `<span style="color:${item.color()}">▬</span> ${escapeHtml(item.label)} ` +
          `<span class="tip-v">${escapeHtml(item.fmt(v ?? NaN))}</span>`,
      );
    }
    this.tip.innerHTML = parts.join("<br>");
    this.tip.style.display = "block";

    const over = u.over.getBoundingClientRect();
    const rootRect = this.root.getBoundingClientRect();
    const left = over.left - rootRect.left + (u.cursor.left ?? 0);
    const top = over.top - rootRect.top + (u.cursor.top ?? 0);
    const w = this.tip.offsetWidth;
    const h = this.tip.offsetHeight;
    const maxX = rootRect.width - w - 4;
    this.tip.style.left = `${Math.max(2, Math.min(maxX, left + 12))}px`;
    this.tip.style.top = `${Math.max(2, top - h - 8)}px`;
  }

  // --- Data ------------------------------------------------------------------

  update(stats: StatsData): void {
    this.last = stats;
    for (const g of this.groups) if (g.details.open) this.refreshGroup(g);
  }

  private refreshGroup(g: GroupEntry): void {
    const s = this.last;
    if (!s) return;
    for (const e of g.entries) {
      if (e.kind === "time") {
        const data: uPlot.AlignedData = [s.t, ...e.build.map((b) => b(s))] as uPlot.AlignedData;
        e.plot.setData(data);
      } else {
        e.phase.draw(s, this.hoverIdx);
      }
      this.setLegend(e.legend);
    }
  }

  /** Re-read every legend at the current cursor (or the latest sample). */
  private refreshAll(): void {
    for (const e of this.timeEntries) if (e.group.details.open) this.setLegend(e.legend);
    for (const e of this.phaseEntries) {
      if (!e.group.details.open) continue;
      this.setLegend(e.legend);
      e.phase.draw(this.last, this.hoverIdx);
    }
  }

  private setLegend(items: LegendItem[]): void {
    const s = this.last;
    const n = s ? s.t.length : 0;
    const i =
      n === 0 ? -1 : this.hoverIdx != null && this.hoverIdx < n ? this.hoverIdx : n - 1;
    for (const item of items) {
      const txt = i < 0 ? "–" : item.fmt(s![item.key][i] ?? NaN);
      if (item.value.textContent !== txt) item.value.textContent = txt;
    }
  }

  // --- Layout ----------------------------------------------------------------

  resize(width: number): void {
    if (width > 0) this.width = width;
    invalidateTokens(); // a theme swap flows through on the next paint
    for (const g of this.groups) {
      if (!g.details.open) continue;
      this.resizeGroup(g);
    }
    this.tip.style.display = "none";
    for (const g of this.groups) if (g.details.open) this.refreshGroup(g);
  }

  private resizeGroup(g: GroupEntry): void {
    const h = this.plotHeight();
    for (const e of g.entries) {
      if (e.kind === "time") {
        const w = e.mount.clientWidth || this.plotWidth();
        e.plot.setSize({ width: w, height: h });
        for (const item of e.legend) item.swatch.style.background = item.color();
      } else {
        const w = e.phase.el.parentElement?.clientWidth || this.plotWidth();
        e.phase.setSize(w, this.phaseHeight());
        e.phase.draw(this.last, this.hoverIdx);
        for (const item of e.legend) item.swatch.style.background = item.color();
      }
    }
  }
}

// ---------------------------------------------------------------------------

/** mean ± sd into a reused buffer. */
function fillEdge(dst: number[], mean: number[], sd: number[], sign: number): number[] {
  const n = mean.length;
  if (dst.length !== n) dst.length = n;
  for (let i = 0; i < n; i++) dst[i] = mean[i] + sign * (sd[i] ?? 0);
  return dst;
}

function emptyData(seriesCount: number): uPlot.AlignedData {
  const out: number[][] = [];
  for (let i = 0; i < seriesCount; i++) out.push([]);
  return out as unknown as uPlot.AlignedData;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : "&quot;",
  );
}
